import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SERVICES, SECTORS } from "@/lib/content";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 transition-colors focus:border-teal focus:outline-none";

export default function EnquiryForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "General enquiry", sector: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you — your enquiry has been sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", service: "General enquiry", sector: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
      <div className={compact ? "space-y-5" : "grid gap-5 sm:grid-cols-2"}>
        <div>
          <label htmlFor="enquiry-name" className="mb-1.5 block text-sm font-semibold text-slate-700">Full name *</label>
          <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} className={inputCls} value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label htmlFor="enquiry-email" className="mb-1.5 block text-sm font-semibold text-slate-700">Work email *</label>
          <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label htmlFor="enquiry-company" className="mb-1.5 block text-sm font-semibold text-slate-700">Organisation</label>
          <input id="enquiry-company" data-testid="enquiry-company-input" className={inputCls} value={form.company} onChange={set("company")} />
        </div>
        <div>
          <label htmlFor="enquiry-service" className="mb-1.5 block text-sm font-semibold text-slate-700">Service of interest</label>
          <select id="enquiry-service" data-testid="enquiry-service-select" className={inputCls} value={form.service} onChange={set("service")}>
            <option>General enquiry</option>
            {SERVICES.map((s) => (
              <option key={s.id}>{s.title}</option>
            ))}
          </select>
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label htmlFor="enquiry-sector" className="mb-1.5 block text-sm font-semibold text-slate-700">Sector</label>
          <select id="enquiry-sector" data-testid="enquiry-sector-select" className={inputCls} value={form.sector} onChange={set("sector")}>
            <option value="">Select a sector…</option>
            {SECTORS.map((s) => (
              <option key={s}>{s}</option>
            ))}
            <option>Other</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="enquiry-message" className="mb-1.5 block text-sm font-semibold text-slate-700">How can we help? *</label>
        <textarea id="enquiry-message" data-testid="enquiry-message-input" required minLength={10} rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your organisation, your safety culture challenges, your performance struggles and what you'd like to change. Leave your mobile number if you prefer a call back or we'll respond via email and you can decide if you want to follow it up." />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="enquiry-form-submit-button"
        className="w-full sm:w-auto rounded-full bg-teal px-8 py-4 text-base font-bold text-white transition-all duration-200 hover:bg-navy hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
      >
        {sending ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );
}
